/**
 * Header refeito do WorkspaceCasoPage (E13.F7c.T02). // ux-neutrality:allow doc-comment
 *
 * Breadcrumb 'Casos / {display_code} / Análise' + chips status (LRPill ENFSI + // ux-neutrality:allow doc-comment
 * StatusPill) + 2 CTAs (Gerar laudo + Compartilhar com revisor (E24.F4)). // ux-neutrality:allow doc-comment
 * E27.F1 removeu o CTA placeholder Exportar ZIP. // ux-neutrality:allow doc-comment
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button, buttonVariants } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { LRPill } from '@/components/ui/LRPill';
import { Spinner } from '@/components/ui/Spinner';
import { CaseStageStepper } from '@/components/Casos/CaseStageStepper';
import { StatusPill, type CaseStatus as UIStatusPillStatus } from '@/components/ui/StatusPill';
import { useToast } from '@/components/ui/Toast';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import type { CaseRecordData, CaseStatus as BackendCaseStatus } from '@/lib/cases';
import type { CaseAnalysisData } from '@/lib/case_workspace';
import { LaudoShareError, shareLaudo } from '@/lib/laudo_share';
import { cn } from '@/lib/utils';

const BACKEND_STATUS_TO_UI: Record<BackendCaseStatus, UIStatusPillStatus> = {
  draft: 'rascunho',
  analysing: 'em_analise',
  done: 'concluido',
  conflict: 'aguardando_revisao',
};

interface LaudoCtaProps {
  caseId: string;
  ready: boolean;
}

function LaudoCta({ caseId, ready }: LaudoCtaProps): JSX.Element {
  if (!ready) {
    return (
      <p
        className={cn(
          buttonVariants({ variant: 'secondary', size: 'sm' }),
          'cursor-not-allowed text-fg-muted opacity-70',
        )}
        data-testid="laudo-cta-disabled"
      >
        {ptBR.workspaceCaso.ctaGerarLaudoBlocked}
      </p>
    );
  }
  // O CTA de saída da mesa é o único objeto sólido do cabeçalho. É um `<Link>`
  // pintado com `buttonVariants` — navegação continua sendo âncora, e a pintura
  // vem do primitivo sem a indireção do Slot.
  return (
    <Link
      to={`/casos/${caseId}/laudo`}
      className={cn(buttonVariants({ variant: 'primary', size: 'sm' }), 'no-underline')}
      data-testid="laudo-cta"
    >
      {ptBR.workspaceCaso.ctaGerarLaudo}
    </Link>
  );
}

function shareErrorMessage(err: unknown): string {
  if (!(err instanceof LaudoShareError)) return ptBR.workspaceCaso.shareErrorGeneric;
  switch (err.reason) {
    case 'no_reviewers':
      return ptBR.workspaceCaso.shareErrorNoReviewers;
    case 'not_ready':
      return ptBR.workspaceCaso.shareErrorNotReady;
    case 'not_configured':
      return ptBR.workspaceCaso.shareErrorNotConfigured;
    case 'send_failed':
      return ptBR.workspaceCaso.shareErrorSendFailed;
    default:
      return ptBR.workspaceCaso.shareErrorGeneric;
  }
}

interface ShareLaudoButtonProps {
  caseId: string;
  /** Compartilhar só faz sentido com a análise concluída (mesmo gate do laudo). */
  ready: boolean;
}

function ShareLaudoButton({ caseId, ready }: ShareLaudoButtonProps): JSX.Element {
  const apiClient = useApiClient();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  async function handleConfirm(): Promise<void> {
    setSending(true);
    try {
      const result = await shareLaudo(apiClient, caseId);
      setConfirmOpen(false);
      const firstRecipient = result.recipients[0] ?? '';
      const description =
        result.recipients.length === 1
          ? ptBR.workspaceCaso.shareSuccessOne.replace('{email}', firstRecipient)
          : ptBR.workspaceCaso.shareSuccessMany.replace(
              '{count}',
              String(result.recipients.length),
            );
      toast({ title: ptBR.workspaceCaso.shareSuccessTitle, description, variant: 'success' });
    } catch (err) {
      setConfirmOpen(false);
      toast({
        title: ptBR.workspaceCaso.shareErrorTitle,
        description: shareErrorMessage(err),
        variant: 'warning',
        duration: 8000,
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={!ready}
        onClick={() => setConfirmOpen(true)}
        data-testid="workspace-cta-compartilhar"
      >
        {ptBR.workspaceCaso.ctaCompartilhar}
      </Button>
      <ConfirmModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={ptBR.workspaceCaso.shareConfirmTitle}
        description={ptBR.workspaceCaso.shareConfirmDescription}
        confirmLabel={ptBR.workspaceCaso.shareConfirmCta}
        cancelLabel={ptBR.workspaceCaso.shareCancel}
        onConfirm={() => {
          void handleConfirm();
        }}
        busy={sending}
      />
    </>
  );
}

export interface WorkspaceHeaderProps {
  caseId: string;
  caseRecord: CaseRecordData | null;
  analysis: CaseAnalysisData;
}

export function WorkspaceHeader({
  caseId,
  caseRecord,
  analysis,
}: WorkspaceHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const uiStatus = caseRecord ? BACKEND_STATUS_TO_UI[caseRecord.status] : null;
  const statusLabel = uiStatus ? ptBR.caseStatus[uiStatus] : null;
  const breadcrumbLabel = caseRecord?.display_code ?? caseId;

  return (
    <div className="workspace-page__topbar" data-testid="workspace-topbar">
      {/* Linha 1: Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        data-testid="workspace-breadcrumb"
        className="text-body-sm text-fg-muted"
      >
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to="/casos" className="hover:text-fg" data-testid="workspace-breadcrumb-casos">
              {ptBR.workspaceCaso.breadcrumbCasos}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link
              to={`/casos/${caseId}`}
              className="hover:text-fg"
              data-testid="workspace-breadcrumb-caso"
            >
              {breadcrumbLabel}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-fg" aria-current="page">
            {ptBR.workspaceCaso.breadcrumbAnalise}
          </li>
        </ol>
      </nav>

      {/* Esteira de etapas do caso (E47.F1.T04) — mesa é a etapa ativa aqui. */}
      <CaseStageStepper
        caseId={caseId}
        workflowStatus={caseRecord?.workflow_status ?? null}
        activeStage="mesa"
      />

      {/* Linha 2: Título + badges + CTAs */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Bloco esquerdo: título + LR + status */}
        <div className="flex flex-wrap items-baseline gap-sc-3">
          {/* 34px da prancha 2B — o degrau `display-md` do sistema. */}
          <h1 className="text-display-md text-fg" data-testid="workspace-page-title">
            {ptBR.workspaceCaso.pageTitle}
          </h1>
          {analysis.composite_lr !== null ? (
            <LRPill
              lr={analysis.composite_lr}
              supportIndex={analysis.composite_lr_support_index}
              data-testid="workspace-header-lr"
            />
          ) : null}
          {uiStatus && statusLabel ? (
            <StatusPill
              status={uiStatus}
              label={statusLabel}
              data-testid="workspace-header-status"
            />
          ) : null}
          {analysis.status === 'running' ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-body-sm text-accent-700"
              data-testid="workspace-header-analysing"
            >
              <Spinner size="sm" />
              {ptBR.workspaceCaso.caseInAnalysis}
            </span>
          ) : null}
        </div>

        {/* Bloco direito: CTAs agrupados */}
        <div className="flex flex-wrap items-center gap-2" data-testid="workspace-header-ctas">
          {/* E34.F2: a jornada Wizard→Mesa deixou de ser só de ida — o perito
              pode voltar ao caso (dados/documentos/assinaturas) e seguir. O
              estado do wizard é preservado (store por caso). */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/casos/${caseId}/wizard`)}
            data-testid="workspace-back-to-case"
          >
            {ptBR.workspaceCaso.ctaVoltarCaso}
          </Button>
          {/* Prancha 2B: o primário fecha a linha, à direita de tudo. */}
          <ShareLaudoButton caseId={caseId} ready={analysis.status === 'completed'} />
          <LaudoCta caseId={caseId} ready={analysis.status === 'completed'} />
        </div>
      </div>
    </div>
  );
}
